import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Eye, 
  ChevronLeft, 
  ChevronRight,
  Filter,
  Calendar,
  Image as ImageIcon
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import toast from 'react-hot-toast';
import apiService from '../../utils/api';

const AdminBlog = () => {
  // Posts list state
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentCategory, setCurrentCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Modal & Form states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: '',
    tags: '',
    image: null,
    published: true
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  // Quill editor modules
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link', 'image'],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ],
  };

  // Fetch posts & categories
  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [currentPage, searchTerm, currentCategory]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, search: searchTerm, category: currentCategory };
      const response = await apiService.getBlogPosts(params);
      setPosts(response.data.posts);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiService.getCategories('blog');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Modal open functions
  const openNewPostModal = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      category: '',
      tags: '',
      image: null,
      published: true
    });
    setPreviewImage(null);
    setIsModalOpen(true);
  };

  const openEditPostModal = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt,
      category: post.category,
      tags: post.tags?.join(', ') || '',
      image: null,
      published: post.published
    });
    setPreviewImage(post.image_url || null);
    setIsModalOpen(true);
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file' && files[0]) {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
      setPreviewImage(URL.createObjectURL(files[0]));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleQuillChange = (content) => {
    setFormData(prev => ({ ...prev, content }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);

    try {
      const postData = {
        title: formData.title,
        content: formData.content,
        excerpt: formData.excerpt,
        category: formData.category,
        tags: formData.tags,
        published: formData.published,
        image: formData.image
      };

      if (editingPost) {
        await apiService.updateBlogPost(editingPost.id, postData);
        setPosts(prev => prev.map(p => p.id === editingPost.id ? { ...p, ...postData } : p));
        toast.success('Post updated successfully!');
      } else {
        const newPost = await apiService.createBlogPost(postData);
        setPosts(prev => [newPost.data, ...prev]);
        toast.success('Post created successfully!');
      }

      setIsModalOpen(false);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.data?.message || 'Failed to save post');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await apiService.deleteBlogPost(id);
        setPosts(prev => prev.filter(post => post.id !== id));
        toast.success('Post deleted successfully!');
      } catch (error) {
        console.error('Error deleting post:', error);
        toast.error('Failed to delete post');
      }
    }
  };

  // Loading skeleton component
  const TableSkeleton = () => (
    <div className="space-y-4">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center space-x-4 animate-pulse">
          <div className="w-10 h-10 bg-gray-300 dark:bg-gray-700 rounded-lg"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-3/4"></div>
            <div className="h-3 bg-gray-300 dark:bg-gray-700 rounded w-1/2"></div>
          </div>
          <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-20"></div>
          <div className="h-6 bg-gray-300 dark:bg-gray-700 rounded w-16"></div>
          <div className="h-8 bg-gray-300 dark:bg-gray-700 rounded w-24"></div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50/30 dark:bg-gray-900/30 ">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Blog Management</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">Create and manage your blog posts</p>
          </div>
          <button 
            onClick={openNewPostModal} 
            className="btn-primary flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
          >
            <Plus size={20} />
            New Post
          </button>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6 mb-8">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 w-full lg:max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={20} className="text-gray-400 dark:text-gray-500" />
            </div>
            <input
              type="text"
              placeholder="Search posts by title or content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-300/80 dark:border-gray-600/80 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
            />
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-3 w-full lg:w-auto">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <Filter size={18} />
              <span className="font-medium">Filter:</span>
            </div>
            <select
              value={currentCategory}
              onChange={(e) => setCurrentCategory(e.target.value)}
              className="flex-1 lg:flex-none px-4 py-3 rounded-xl border border-gray-300/80 dark:border-gray-600/80 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Posts Table Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
        {/* Table Header */}
        <div className="px-6 py-4 border-b border-gray-200/50 dark:border-gray-700/50">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Blog Posts</h3>
        </div>

        {/* Table Content */}
        <div className="p-6">
          {loading ? (
            <TableSkeleton />
          ) : posts.length > 0 ? (
            <div className="overflow-hidden">
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200/50 dark:border-gray-700/50">
                      <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">Post</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">Category</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">Date</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">Status</th>
                      <th className="text-right py-4 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200/50 dark:divide-gray-700/50">
                    {posts.map(post => (
                      <tr key={post.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-750/50 transition-colors duration-150">
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 overflow-hidden border border-gray-200/50 dark:border-gray-600/50">
                              {post.image_url ? (
                                <img 
                                  src={`https://backend.ysskytravel.com${post.image_url}`} 
                                  alt={post.title} 
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <ImageIcon size={20} className="text-gray-400 dark:text-gray-500" />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                {post.title}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
                                {post.excerpt || 'No excerpt provided'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                            {post.category || 'Uncategorized'}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <Calendar size={14} className="mr-1.5" />
                            {new Date(post.publish_date).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            post.published 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {post.published ? 'Published' : 'Draft'}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex justify-end space-x-2">
                            <a 
                              href={`/news/${post.id}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center p-2 text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all duration-200"
                              title="View"
                            >
                              <Eye size={16} />
                            </a>
                            
                            <button
                              onClick={() => handleDelete(post.id)}
                              className="inline-flex items-center p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4">
                {posts.map(post => (
                  <div key={post.id} className="bg-gray-50/50 dark:bg-gray-750/50 rounded-xl p-4 border border-gray-200/50 dark:border-gray-700/50">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 overflow-hidden border border-gray-200/50 dark:border-gray-600/50">
                        {post.image_url ? (
                          <img 
                            src={`https://backend.ysskytravel.com${post.image_url}`} 
                            alt={post.title} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon size={20} className="text-gray-400 dark:text-gray-500" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {post.title}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                          {post.excerpt || 'No excerpt provided'}
                        </p>
                        <div className="flex items-center mt-2 space-x-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                            {post.category || 'Uncategorized'}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            post.published 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {post.published ? 'Published' : 'Draft'}
                          </span>
                        </div>
                        <div className="flex items-center mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <Calendar size={12} className="mr-1" />
                          {new Date(post.publish_date).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2 mt-4 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
                      <a 
                        href={`/blog/${post.id}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center p-2 text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all duration-200"
                        title="View"
                      >
                        <Eye size={16} />
                      </a>
                      <button
                        onClick={() => openEditPostModal(post)}
                        className="inline-flex items-center p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200"
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="inline-flex items-center p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <Search size={32} className="text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No posts found</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
                {searchTerm || currentCategory ? 'Try adjusting your search or filter to find what you are looking for.' : 'Get started by creating your first blog post.'}
              </p>
              <button onClick={openNewPostModal} className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold">
                <Plus size={20} />
                Create New Post
              </button>
            </div>
          )}

          {/* Pagination */}
          {posts.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-8 pt-6 border-t border-gray-200/50 dark:border-gray-700/50 gap-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Showing <span className="font-semibold">{(currentPage - 1) * 10 + 1}-{Math.min(currentPage * 10, posts.length)}</span> of{' '}
                <span className="font-semibold">{posts.length}</span> posts
              </p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`inline-flex items-center p-2 rounded-lg border transition-all duration-200 ${
                    currentPage === 1 
                      ? 'text-gray-400 border-gray-300 dark:border-gray-600 cursor-not-allowed' 
                      : 'text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
                  }`}
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`inline-flex items-center p-2 rounded-lg border transition-all duration-200 ${
                    currentPage === totalPages 
                      ? 'text-gray-400 border-gray-300 dark:border-gray-600 cursor-not-allowed' 
                      : 'text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
                  }`}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal for Create/Edit */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl my-8 relative max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200/50 dark:border-gray-700/50 bg-white dark:bg-gray-800 sticky top-0 z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {editingPost ? 'Edit Post' : 'Create New Post'}
                </h2>
                <p className="text-gray-500 dark:text-gray-400 mt-1">
                  {editingPost ? 'Update your blog post details' : 'Fill in the details to create a new blog post'}
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <span className="text-2xl font-light">&times;</span>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto">
              <form onSubmit={handleFormSubmit} className="p-6 space-y-6">
                {/* Post Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Post Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter a compelling title for your post"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300/80 dark:border-gray-600/80 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
                  />
                </div>

                {/* Excerpt */}
                <div>
                  <label htmlFor="excerpt" className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Excerpt
                  </label>
                  <textarea
                    id="excerpt"
                    name="excerpt"
                    rows="3"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    placeholder="Brief description of your post (appears in post listings)"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300/80 dark:border-gray-600/80 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 resize-none"
                  ></textarea>
                </div>

                {/* Content */}
                <div>
                  <label htmlFor="content" className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Content *
                  </label>
                  <div className="rounded-xl overflow-hidden border border-gray-300/80 dark:border-gray-600/80 bg-white dark:bg-gray-800">
                    <ReactQuill
                      theme="snow"
                      value={formData.content}
                      onChange={handleQuillChange}
                      modules={quillModules}
                      className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                {/* Category & Tags */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="category" className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                      Category
                    </label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-300/80 dark:border-gray-600/80 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
                    >
                      <option value="">Select a category</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.name}>{category.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="tags" className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                      Tags
                    </label>
                    <input
                      type="text"
                      id="tags"
                      name="tags"
                      value={formData.tags}
                      onChange={handleInputChange}
                      placeholder="technology, web, design (comma separated)"
                      className="w-full px-4 py-3 rounded-xl border border-gray-300/80 dark:border-gray-600/80 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
                    />
                  </div>
                </div>

                {/* Featured Image */}
                <div>
                  <label htmlFor="image" className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Featured Image
                  </label>
                  <div className="flex flex-col md:flex-row items-start gap-6">
                    <div className="flex-1">
                      <div className="border-2 border-dashed border-gray-300/80 dark:border-gray-600/80 rounded-xl p-6 text-center hover:border-primary-500/50 dark:hover:border-primary-500/50 transition-all duration-200 bg-gray-50/50 dark:bg-gray-750/50">
                        <input
                          type="file"
                          id="image"
                          name="image"
                          accept="image/*"
                          onChange={handleInputChange}
                          className="hidden"
                        />
                        <label htmlFor="image" className="cursor-pointer block">
                          <ImageIcon size={32} className="mx-auto text-gray-400 dark:text-gray-500 mb-2" />
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Click to upload featured image
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Recommended: 1200 × 630 pixels
                          </p>
                        </label>
                      </div>
                    </div>
                    {previewImage && (
                      <div className="w-32 h-32 rounded-xl overflow-hidden border border-gray-300/80 dark:border-gray-600/80 bg-white dark:bg-gray-800 flex-shrink-0">
                        <img src={previewImage} alt="Preview" className="w-full h-full object-cover" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Publish Toggle */}
                <div className="flex items-center p-4 rounded-xl bg-gray-50/50 dark:bg-gray-750/50 border border-gray-200/50 dark:border-gray-700/50">
                  <input
                    type="checkbox"
                    id="published"
                    name="published"
                    checked={formData.published}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-primary-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500/20 focus:ring-2"
                  />
                  <label htmlFor="published" className="ml-3 text-sm font-medium text-gray-900 dark:text-white">
                    Publish this post immediately
                  </label>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0 pt-6 border-t border-gray-200/50 dark:border-gray-700/50 sticky bottom-0 bg-white dark:bg-gray-800 pb-2">
                  <button 
                    type="submit" 
                    disabled={formSubmitting}
                    className="w-full sm:w-auto btn-primary px-8 py-3 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {formSubmitting ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Saving...
                      </div>
                    ) : (
                      editingPost ? 'Update Post' : 'Create Post'
                    )}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setIsModalOpen(false)}
                    className="w-full sm:w-auto px-8 py-3 rounded-xl font-semibold border border-gray-300/80 dark:border-gray-600/80 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;